import type { InfiniteData, QueryClient } from "@tanstack/vue-query";
import type { HydraCollection, PartialHydraCollection } from "../Api/Type/hydra.interface";

type HydraPage<T> = HydraCollection<T> | PartialHydraCollection<T>;

function isInfiniteData<T>(data: unknown): data is InfiniteData<HydraPage<T>, unknown> {
  return typeof data === "object" && data !== null && "pages" in data && Array.isArray((data as InfiniteData<HydraPage<T>>).pages);
}

function isHydraPage<T>(data: unknown): data is HydraPage<T> {
  return typeof data === "object" && data !== null && "member" in data && Array.isArray((data as HydraPage<T>).member);
}

export function hydraDataContainsItem(data: unknown, iri: string): boolean {
  if (isInfiniteData<{ "@id": string }>(data)) {
    return data.pages.some(page => page.member.some(item => item["@id"] === iri));
  }
  if (isHydraPage<{ "@id": string }>(data)) {
    return data.member.some(item => item["@id"] === iri);
  }
  return false;
}

export function patchHydraData<T extends { "@id": string }>(data: unknown, iri: string, patch: Partial<T>): unknown {
  if (isInfiniteData<T>(data)) {
    return {
      ...data,
      pages: data.pages.map(page => ({
        ...page,
        member: page.member.map(item => item["@id"] === iri ? { ...item, ...patch } : item),
      })),
    };
  }
  if (isHydraPage<T>(data)) {
    return {
      ...data,
      member: data.member.map(item => item["@id"] === iri ? { ...item, ...patch } : item),
    };
  }
  return data;
}

export function patchHydraQueries<T extends { "@id": string }>(
  queryClient: QueryClient,
  sourceKey: readonly unknown[],
  iri: string,
  patch: Partial<T>,
): void {
  queryClient.setQueriesData({ queryKey: sourceKey }, data => patchHydraData<T>(data, iri, patch));
}

export function upsertHydraQueryItem<T extends { "@id": string }>(
  queryClient: QueryClient,
  sourceKey: readonly unknown[],
  item: T,
  compare?: (left: T, right: T) => number,
): void {
  queryClient.setQueriesData({ queryKey: sourceKey }, (data) => {
    if (isInfiniteData<T>(data)) {
      const existed = data.pages.some(page => page.member.some(entry => entry["@id"] === item["@id"]));
      const pages = data.pages.map(page => ({
        ...page,
        member: page.member.filter(entry => entry["@id"] !== item["@id"]),
      }));
      const first = pages[0];
      if (!first) {
        return {
          ...data,
          pages: [{ member: [item] } as HydraPage<T>],
        };
      }
      const member = [item, ...first.member] as typeof first.member;
      if (compare) {
        member.sort(compare);
      }
      pages[0] = {
        ...first,
        member,
        ...("totalItems" in first && typeof first.totalItems === "number"
          ? { totalItems: first.totalItems + (existed ? 0 : 1) }
          : {}),
      };
      return { ...data, pages };
    }
    if (isHydraPage<T>(data)) {
      const existed = data.member.some(entry => entry["@id"] === item["@id"]);
      const member = [item, ...data.member.filter(entry => entry["@id"] !== item["@id"])] as typeof data.member;
      if (compare) {
        member.sort(compare);
      }
      if ("totalItems" in data && typeof data.totalItems === "number") {
        return { ...data, member, totalItems: data.totalItems + (existed ? 0 : 1) };
      }
      return { ...data, member };
    }
    return data;
  });
}

export function removeHydraQueryItem<T extends { "@id": string }>(
  queryClient: QueryClient,
  sourceKey: readonly unknown[],
  iri: string,
): void {
  queryClient.setQueriesData({ queryKey: sourceKey }, (data) => {
    if (!hydraDataContainsItem(data, iri)) {
      return data;
    }
    if (isInfiniteData<T>(data)) {
      return {
        ...data,
        pages: data.pages.map((page, index) => {
          const member = page.member.filter(item => item["@id"] !== iri);
          if (index === 0 && "totalItems" in page && typeof page.totalItems === "number") {
            return { ...page, member, totalItems: Math.max(0, page.totalItems - 1) };
          }
          return { ...page, member };
        }),
      };
    }
    if (isHydraPage<T>(data)) {
      const member = data.member.filter(item => item["@id"] !== iri);
      if ("totalItems" in data && typeof data.totalItems === "number") {
        return { ...data, member, totalItems: Math.max(0, data.totalItems - 1) };
      }
      return { ...data, member };
    }
    return data;
  });
}
